import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useAuth } from '@src/context';
import paths from '@src/paths';
import { fetchBillingFleet } from '@src/api/billingThreshold';

const STATE_CHIP_COLORS = {
  healthy: 'success',
  warning: 'warning',
  paused: 'error',
  disabled: 'default',
};

const STATE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'healthy', label: 'Healthy' },
  { value: 'warning', label: 'Warning' },
  { value: 'paused', label: 'Paused' },
  { value: 'disabled', label: 'Disabled' },
];

const ENABLED_FILTERS = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Enabled' },
  { value: 'false', label: 'Disabled' },
];

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

export default function BillingFleetPage() {
  const { state } = useAuth();
  const user = state?.user || {};
  const isStaff = user.is_staff || user.is_superuser;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ summary: null, rows: [] });
  const [stateFilter, setStateFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (stateFilter) params.state = stateFilter;
      if (enabledFilter) params.enabled = enabledFilter;
      if (pendingOnly) params.pending_verification = 'true';
      const resp = await fetchBillingFleet(params);
      setData(resp?.data || { summary: null, rows: [] });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [stateFilter, enabledFilter, pendingOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data.rows || [];
    return (data.rows || []).filter((row) =>
      (row.client_name || '').toLowerCase().includes(term),
    );
  }, [data.rows, search]);

  if (!isStaff) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">
          You don't have permission to view this page. Staff access only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Billing Thresholds — Fleet</Typography>
          <Typography variant="body2" color="text.secondary">
            All clients enrolled in threshold billing. Riskiest accounts at the top.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to={paths.billingActivity}>
            Payment activity
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary header */}
      {data.summary && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Profiles
                </Typography>
                <Typography variant="h6">{data.summary.total_profiles}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total unbilled (live)
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(data.summary.total_unbilled_spend)}
                </Typography>
              </Box>
              {Number(data.summary.total_pending_verifications || 0) > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pending bank verification
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      label={data.summary.total_pending_verifications}
                      color="warning"
                    />
                  </Stack>
                </Box>
              )}
              {Object.entries(data.summary.state_counts || {}).map(([s, n]) => (
                <Box key={s}>
                  <Typography variant="caption" color="text.secondary">
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      label={n}
                      color={STATE_CHIP_COLORS[s] || 'default'}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <ToggleButtonGroup
              size="small"
              value={stateFilter}
              exclusive
              onChange={(_, value) => setStateFilter(value ?? '')}
            >
              {STATE_FILTERS.map((opt) => (
                <ToggleButton key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              select
              size="small"
              label="Enabled"
              value={enabledFilter}
              onChange={(e) => setEnabledFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {ENABLED_FILTERS.map((opt) => (
                <MenuItem key={opt.value || 'any'} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Search client"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <ToggleButton
              size="small"
              value="pending"
              selected={pendingOnly}
              onChange={() => setPendingOnly((v) => !v)}
              color="warning"
            >
              Pending verification only
            </ToggleButton>
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : filteredRows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No billing profiles match the current filters.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell align="right">Unbilled / Threshold</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Failures</TableCell>
                    <TableCell align="right">Paused FB</TableCell>
                    <TableCell>Verification</TableCell>
                    <TableCell>Last charge</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row) => {
                    const pct = Math.max(
                      0,
                      Math.min(100, row.threshold_progress_pct || 0),
                    );
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          bgcolor:
                            row.state === 'paused'
                              ? 'error.lighter'
                              : row.state === 'warning'
                              ? 'warning.lighter'
                              : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={paths.clientBilling(row.client_id)}
                            underline="hover"
                          >
                            {row.client_name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.state_display || row.state}
                            color={STATE_CHIP_COLORS[row.state] || 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {row.threshold_billing_enabled ? (
                            <Chip size="small" label="On" color="success" />
                          ) : (
                            <Chip size="small" label="Off" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(row.unbilled_spend)} /{' '}
                          {formatCurrency(row.threshold_amount)}
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={pct >= 100 ? 'error' : pct >= 75 ? 'warning' : 'primary'}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {pct.toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {row.consecutive_payment_failures || 0}
                        </TableCell>
                        <TableCell align="right">
                          {row.paused_campaign_count || 0}
                        </TableCell>
                        <TableCell>
                          {Number(row.pending_bank_verifications || 0) > 0 ? (
                            <Chip
                              size="small"
                              label={`${row.pending_bank_verifications} pending`}
                              color="warning"
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(row.last_threshold_charge_at)}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            component={RouterLink}
                            to={paths.clientBilling(row.client_id)}
                          >
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
